import React, { useMemo, useState } from "react";
import { StandardPageWrapper } from "../../../../components/layout/StandardPageWrapper";
import { Routes } from "../../../../root/routes";
import { usePrimaryOrganizationId } from "../../../../hooks";
import { Breadcrumbs, DateDisplay, Modal, Pill, Table } from "@truevault/aquilo";
import { Action, ActionStyle, TableDensity } from "@truevault/aquilo/src/components/table/Table";

import { useAuditLogs } from "../../../../../common/hooks/useAuditLogs";
import { AuditLogEntryDto } from "../../../../../common/service/server/dto/AuditLogEntryDto";
import { InformationCircleIcon } from "@heroicons/react/20/solid";
import { DateDisplayVariant } from "@truevault/aquilo/src/components/dateDisplay/DateDisplay";

/**
 * Take some of the common values for audit log values like "INTERACTIVE_DEFAULT_CONSENT" and translate them to words.
 * @param value The value to translate, e.g. "INTERACTIVE_DEFAULT_CONSENT".
 */
const humanReadableValue = (value: string): string => {
  switch (value) {
    case "INTERACTIVE_DEFAULT_CONSENT":
      return "Opt-Out Banner";
    case "INTERACTIVE_DEFAULT_NO_CONSENT":
      return "Opt-In Banner";
    case "INFORMATIONAL_ONLY":
      return "Informational Banner";
    case "NONE":
      return "None";
    default:
      return value;
  }
};

export const AuditLogsPage = () => {
  const orgId = usePrimaryOrganizationId();
  const [auditLogs, request, _refresh] = useAuditLogs(orgId);

  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntryDto | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const columns = useMemo(
    () => [
      {
        key: "changeTitle",
        header: "Change Made",
        isVisuallyPrimary: true,
      },
      {
        key: "userName",
        header: "By User",
      },
      {
        key: "createdAt",
        header: "When",
        render: (row: AuditLogEntryDto) => (
          <DateDisplay date={row.createdAt} hoverForRelative={true} />
        ),
      },
    ],
    [],
  );

  const rowActions: Action<AuditLogEntryDto>[] = useMemo(
    () => [
      {
        label: "See Details",
        icon: <InformationCircleIcon className="h-5 w-5 text-gray-500" />,
        onClick: (auditLogEntry) => {
          setSelectedEntry(auditLogEntry);
          setDialogOpen(true);
        },
      },
    ],
    [],
  );

  return (
    <StandardPageWrapper requests={[request]} className="tw-boundary">
      <Breadcrumbs title="Audit Logs" source="Privacy Docs" sourceRoute={Routes.documents.root} />

      <Table
        data={auditLogs || []}
        columns={columns}
        actions={rowActions}
        actionsLabel="Details"
        tableDensity={TableDensity.COMPACT}
        actionStyle={ActionStyle.ICONS}
        keyExtractor={(row) => row.id}
      />
      {dialogOpen && (
        <Modal
          onClose={() => {
            setDialogOpen(false);
            setSelectedEntry(null);
          }}
          title={selectedEntry?.changeTitle ?? "Details"}
          alignText={"left"}
          contentContainerClasses="w-full"
          closeButton={true}
          showOverlay={true}
          closeOnOverlayClick={true}
        >
          <small>
            <div>
              <strong className="mr-1">User:</strong>
              <span>{selectedEntry?.userName}</span>
            </div>
            <div className="mt-1">
              <strong className="mr-1">Email:</strong>
              <span>{selectedEntry?.userEmail || "N/A"}</span>
            </div>
            <div className="mt-1 flex">
              <strong className="mr-1">Timestamp:</strong>
              <DateDisplay date={selectedEntry?.createdAt} variant={DateDisplayVariant.FULL} />
            </div>
          </small>

          <div className="mt-4">
            {selectedEntry?.changeValueAfter !== null && (
              <div className="w-full">
                <small>
                  <strong>After</strong>
                </small>
                <Pill variant="completed">New</Pill>
                <textarea
                  readOnly={true}
                  rows={3}
                  className="w-full p-2 border border-gray-300 rounded mt-1 mb-2"
                >
                  {humanReadableValue(selectedEntry?.changeValueAfter)}
                </textarea>
              </div>
            )}
            {selectedEntry?.changeValueBefore !== null && (
              <div className="w-full">
                <small>
                  <strong>Before</strong>
                </small>
                <Pill variant="error">
                  {selectedEntry?.changeValueAfter === null ? "Deleted" : "Old"}
                </Pill>
                <textarea
                  readOnly={true}
                  rows={3}
                  className="w-full p-2 border border-gray-300 rounded mt-1"
                >
                  {humanReadableValue(selectedEntry?.changeValueBefore)}
                </textarea>
              </div>
            )}
          </div>
        </Modal>
      )}
    </StandardPageWrapper>
  );
};
